// pages/admin/AdminContactSubmissions.js

import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
} from "react";

import {
    Modal,
    Button,
    Alert,
    Badge,
} from "react-bootstrap";

import { FiEye } from "react-icons/fi";

import ReusableAdminTable from "./ReusableAdminTable";

import {
    fetchContactSubmissionsApi,
    fetchContactSubmissionDetailsApi,
} from "../../services/adminApi";

import "./AdminContactSubmissions.css";

const AdminContactSubmissions = () => {
    // =========================================
    // STATES
    // =========================================
    const [submissions, setSubmissions] = useState([]);

    const [selectedSubmission, setSelectedSubmission] =
        useState(null);

    const [isLoading, setIsLoading] = useState(true);

    const [error, setError] = useState("");

    const [showModal, setShowModal] = useState(false);

    // =========================================
    // LOAD SUBMISSIONS
    // =========================================
    const loadSubmissions = useCallback(async () => {
        setIsLoading(true);
        setError("");

        try {
            const res = await fetchContactSubmissionsApi();

            const responseData = res?.data || [];

            setSubmissions(responseData);
        } catch (err) {
            console.error(
                "Error loading contact submissions:",
                err
            );

            setError(
                `Failed to load submissions: ${
                    err.message ||
                    "An unknown error occurred."
                }`
            );

            setSubmissions([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // =========================================
    // INITIAL LOAD
    // =========================================
    useEffect(() => {
        loadSubmissions();
    }, [loadSubmissions]);

    // =========================================
    // VIEW DETAILS
    // =========================================
    const handleViewDetails = async (id) => {
        if (!id) {
            alert("Submission ID not found.");
            return;
        }

        try {
            const res =
                await fetchContactSubmissionDetailsApi(id);

            setSelectedSubmission(res?.data || null);

            setShowModal(true);
        } catch (err) {
            console.error(
                "Error loading submission details:",
                err
            );

            setError(
                `Failed to load submission details: ${
                    err.message ||
                    "An unknown error occurred."
                }`
            );
        }
    };

    // =========================================
    // CLOSE MODAL
    // =========================================
    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedSubmission(null);
    };

    // =========================================
    // TABLE COLUMNS
    // =========================================
    const columns = useMemo(
        () => [
            {
                accessorKey: "id",
                header: "#",
                cell: (info) => info.row.index + 1,
            },

            {
                id: "fullName",
                header: "Name",
                cell: (info) =>
                    `${info.row.original.firstName || ""} ${
                        info.row.original.lastName || ""
                    }`,
            },

            {
                accessorKey: "email",
                header: "Email",
            },

            {
                accessorKey: "mobile",
                header: "Mobile",
            },

            {
                accessorKey: "status",
                header: "Status",
                cell: (info) => {
                    const status = info.getValue();

                    return (
                        <Badge
                            bg={
                                status === "New"
                                    ? "primary"
                                    : "secondary"
                            }
                        >
                            {status || "N/A"}
                        </Badge>
                    );
                },
            },

            {
                accessorKey: "created_at",
                header: "Received On",
                cell: (info) =>
                    info.getValue()
                        ? new Date(
                              info.getValue()
                          ).toLocaleString()
                        : "-",
            },

            {
                id: "actions",
                header: () => <div style={{ textAlign: "center", width: "100%" }}>ACTIONS</div>,
                cell: (info) => (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "0 auto" }}>
                        <button
                            type="button"
                            style={{
                                background: "#2563eb",
                                backgroundColor: "#2563eb",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "6px",
                                width: "32px",
                                height: "32px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                padding: 0,
                                flexShrink: 0
                            }}
                            onClick={() =>
                                handleViewDetails(
                                    info.row.original.id
                                )
                            }
                            title="View Details"
                        >
                            <FiEye size={15} color="#ffffff" />
                        </button>
                    </div>
                ),
            },
        ],
        []
    );

    // =========================================
    // RENDER
    // =========================================
    return (
        <div>
            {/* =========================================
                ERROR
            ========================================= */}
            {error && (
                <Alert variant="danger">
                    {error}
                </Alert>
            )}

            {/* =========================================
                TABLE
            ========================================= */}
            <ReusableAdminTable
                data={submissions}
                columns={columns}
                isLoading={isLoading}
                title="Contact Form Submissions"
                searchPlaceholder="Search submissions..."
                exportCsvFilename="contact-submissions.csv"
            />

            {/* =========================================
                DETAILS MODAL
            ========================================= */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                centered
                data-bs-theme="dark"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Submission Details
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedSubmission ? (
                        <div>
                            <p>
                                <strong>Name:</strong>{" "}
                                {
                                    selectedSubmission.firstName
                                }{" "}
                                {
                                    selectedSubmission.lastName
                                }
                            </p>

                            <p>
                                <strong>Email:</strong>{" "}
                                {
                                    selectedSubmission.email
                                }
                            </p>

                            <p>
                                <strong>Mobile:</strong>{" "}
                                {
                                    selectedSubmission.mobile
                                }
                            </p>

                            <p>
                                <strong>
                                    Received:
                                </strong>{" "}
                                {selectedSubmission.created_at
                                    ? new Date(
                                          selectedSubmission.created_at
                                      ).toLocaleString()
                                    : "-"}
                            </p>

                            <p>
                                <strong>Status:</strong>{" "}
                                <Badge
                                    bg={
                                        selectedSubmission.status ===
                                        "New"
                                            ? "primary"
                                            : "secondary"
                                    }
                                >
                                    {
                                        selectedSubmission.status
                                    }
                                </Badge>
                            </p>

                            <hr />

                            <h5>Message</h5>

                            <div
                                style={{
                                    whiteSpace: "pre-wrap",
                                    lineHeight: "1.7",
                                }}
                            >
                                {
                                    selectedSubmission.message
                                }
                            </div>
                        </div>
                    ) : (
                        <p>Loading details...</p>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCloseModal}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminContactSubmissions;